import { lazy, type ComponentType } from 'react';

const RELOAD_KEY = 'z3_chunk_reload';
const THROTTLE_MS = 15_000;

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = (err as Error)?.message || String(err);
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Failed to load module script')
  );
}

/**
 * Wraps dynamic module imports with automatic recovery.
 *
 * When a deployment replaces asset hashes on Cloudflare Pages/CDN, an already-open tab
 * attempting to lazy-load a route (like /admin/products) will get a 404 on the old hash.
 * safeLazy catches this specific error, checks if we recently reloaded to avoid infinite loops,
 * and reloads the page to fetch the fresh bundle.
 */
export function safeLazy<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err)) {
        const lastReload = sessionStorage.getItem(RELOAD_KEY);
        const now = Date.now();
        if (!lastReload || now - Number(lastReload) > THROTTLE_MS) {
          sessionStorage.setItem(RELOAD_KEY, String(now));
          console.warn('Stale chunk detected after deployment. Reloading latest bundle...', err);
          window.location.reload();
          // Return a hanging promise so React suspends while the browser reloads
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }
  });
}
